import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { AiOutlineCalendar } from 'react-icons/ai'
import { TbLayoutKanban } from 'react-icons/tb'
import {
    MdOutlineChecklist,
    MdOutlineCheckCircle,
    MdOutlineForum,
    MdOutlinePause,
    MdOutlinePlayArrow,
    MdOutlineRestoreFromTrash,
} from 'react-icons/md'

import { resourceManager } from '@/Core/Managers/Resource'
import { notificationService } from '@/Core/Services/NotificationService'

import { openEntityChat } from '@/Containers/CommunicationSection/ChatContainer/openEntityChat'

import { RichTextInput } from '@/Ship/Components/RichText/RichTextInput'
import { renderRichTextView } from '@/Ship/Components/RichText/RichTextView'
import { renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { TASK_STATUS_OPTIONS } from '../Contracts/TaskStatus'
import { formatDate, renderCompletedAt, renderParentLink, renderTimeSpent } from '../Helpers/taskFieldFormat'
import { TaskViewActions } from '../Hooks/TaskViewActions'
import { RestoreTaskTask } from '../Tasks/RestoreTaskTask'
import { TaskFormTemplate } from '../Templates/TaskFormTemplate'

import type { TaskCalendarProps } from '../Components/Calendar/TaskCalendar'
import type { TaskKanbanProps } from '../Components/Kanban/TaskKanban'
import type { TaskCrudContextLike, TaskFormContextLike } from '../Contracts/Crud.types'
import type { TaskPermissions, TaskUserRef } from '../Contracts/Task.types'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFormEventContext } from '@/Core/Contracts/UI/Form/IFormConfig'
import type { ReactNode } from 'react'

// Lazy: канбан (dnd-kit) і календар (fullcalendar) не потрібні, поки
// користувач не перемкнув режим — не вантажимо їх у бандл таблиці
const TaskKanban = lazy(() => import('../Components/Kanban/TaskKanban'))
const TaskCalendar = lazy(() => import('../Components/Calendar/TaskCalendar'))

/** Мінімум headerPage-контексту, потрібний реєстрації режимів списку. */
interface TaskMenuItem {
    key?: string
    label?: ReactNode
    icon?: ReactNode
    sort?: number
    onClick?: () => void
}

interface TaskHeaderPageContextLike {
    storages?: {
        crudContext?: TaskCrudContextLike & {
            listContext?: { variantItems?: Record<string, unknown>; actions?: { load?: (force?: boolean) => void } }
            settings?: Record<string, unknown>
            eventManager?: {
                on: (event: string, name: string, handler: (payload: never) => void) => void
            }
        }
    }
}

const variantFallback = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
    </div>
)

/** Обгортки-шаблони варіантів: template ядро кличе з props варіанта ({crudContext}). */
const LazyKanbanTemplate = (props: TaskKanbanProps) => (
    <Suspense fallback={variantFallback}>
        <TaskKanban {...props} />
    </Suspense>
)

const LazyCalendarTemplate = (props: TaskCalendarProps) => (
    <Suspense fallback={variantFallback}>
        <TaskCalendar {...props} />
    </Suspense>
)

/** Мінімальна форма файлового об'єкта, що потрібна для attach (SPEC-007). */
interface TaskFileLike {
    uuid?: string
    /** Присутнє лише у відповіді upload (група `file:read`), відсутнє у вже прикріплених (`file:short:read`). */
    createdAt?: string
}

/**
 * Підпис користувача в таблиці. Працює з СИРИМ об'єктом зв'язку — колонки
 * `manager`/`responsibles` виключені з ядрової трансформації через
 * `table.dataSourceNoTransform` (див. коментар до `table`).
 */
const userLabel = (u?: TaskUserRef | null): string =>
    u?.shortName || u?.fullName || [u?.lastName, u?.firstName].filter(Boolean).join(' ') || u?.email || '—'

/** Параметри `onButtons` картки (ядро додатково передає `content`, SPEC-008 §8.2). */
interface TaskOnButtonsParams {
    buttons: Record<string, unknown>
    crudContext?: TaskCrudContextLike
    content?: ReactNode[]
}

/** Параметри `depFn` кнопки (ButtonGroup інжектить `formContext` з `depStore`). */
interface TaskButtonDepParams {
    formContext?: TaskFormContextLike
}

/**
 * TaskResource — конфіг ресурсу «Завдання» на новому ядрі (SPEC-008 §4).
 *
 * Поля §4.1: усі, включно з `files` (4.7 — двокроковий upload→attach). Опис
 * (`description`) — rich-text на tiptap: `input: RichTextInput`, read-only показ
 * `view: renderRichTextView` (SPEC-008 §6, ADR 005). Кнопки воркфлоу / модалка / вкладки — 4.6+.
 */
export const TaskResource: IResourceConfig = {
    title: 'Завдання',
    key: 'Task',
    restUrl: '/services/tasks',
    pageUrl: '/tasks',
    fields: {
        number: {
            label: '№',
            type: 'number',
            // Заповнює сервер (SPEC-004 §2.5) — read-only у формі.
            isEditable: false,
            inputProps: { disabled: true },
        },
        title: {
            label: 'Назва',
            isRequired: true,
        },
        description: {
            label: 'Опис',
            isRequired: true,
            // Rich-text опис на tiptap (SPEC-008 §6, ADR 005): редактор — кастомний
            // `input`, зберігає HTML у полі `description`; read-only показ на view-картці —
            // `renderRichTextView` (позиційний контракт view, УРОК 06). НЕ ядровий
            // `type:'editorjs'` (він у ядрі закоментований). Порожній опис RichTextInput
            // зводить до '' → спрацьовує ядровий `isRequired`.
            input: RichTextInput,
            view: renderRichTextView,
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: TASK_STATUS_OPTIONS,
            // Статус змінюють кнопки воркфлоу (4.6), а не форма.
            isEditable: false,
            inputProps: { disabled: true },
        },
        // Люди в перегляді — аватар з ініціалами й ім'я (`renderUsersView`)
        manager: {
            label: 'Керівник',
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        },
        responsibles: {
            label: 'Виконавці',
            type: 'select',
            isMultiple: true,
            isRequired: true,
            resource: 'Setting.User',
            view: renderUsersView,
        },
        supervisors: {
            label: 'Спостерігачі',
            type: 'select',
            isMultiple: true,
            resource: 'Setting.User',
            view: renderUsersView,
        },
        parent: {
            label: 'Батьківське завдання',
            type: 'select',
            resource: 'Task', // self-ресурс (короткий ключ реєструється нижче)
            // На view-картці — посилання: клік відкриває картку батька
            // (подвійний контракт view — обробляє сам renderParentLink)
            view: renderParentLink,
            depFn: ({ field, formContext }) => {
                // Не чіпаємо фільтр; у формі вимикаємо перепризначення батька в існуючого завдання.
                if (formContext?.contextKey === 'filterFormContext') return
                if (formContext?.values?.uuid) field.mergeInputProps({ disabled: true })
            },
        },
        deadline: {
            label: 'Дедлайн',
            type: 'date',
            isRequired: true,
            view: renderDateView,
        },
        isPriority: {
            label: 'Пріоритет',
            type: 'switch',
            defaultValue: false,
        },
        completedAt: {
            label: 'Завершено',
            type: 'datetime',
            isEditable: false,
            inputProps: { disabled: true },
            // Показ лише коли status==='completed' (SPEC-008 §4.1), інакше поле
            // сховане. Подвійний контракт view ядра (позиційний + об'єктний
            // ре-виклик FormField) — обробляє сам renderCompletedAt.
            view: renderCompletedAt,
        },
        timeSpentInMinutes: {
            label: 'Витрачено часу',
            type: 'number',
            // Заповнює модалка завершення (§8.2) — read-only у формі; показ «Nг Mхв»,
            // без значення поле сховане (подвійний контракт view — у renderTimeSpent).
            isEditable: false,
            inputProps: { disabled: true },
            view: renderTimeSpent,
        },
        createdAt: {
            label: 'Дата створення',
            type: 'datetime',
            isEditable: false,
            inputProps: { disabled: true },
            view: renderDateTimeView,
        },
        // Службова інформація картки: бек віддає її для кожного запису
        // (ядрова `ListEntity`, група `list:read`); у формі — лише показ
        createdBy: {
            label: 'Хто створив',
            type: 'select',
            resource: 'Setting.User',
            isEditable: false,
            view: renderUsersView,
        },
        updatedAt: {
            label: 'Дата зміни',
            type: 'datetime',
            isEditable: false,
            view: renderDateTimeView,
        },
        updatedBy: {
            label: 'Хто змінив',
            type: 'select',
            resource: 'Setting.User',
            isEditable: false,
            view: renderUsersView,
        },
        files: {
            label: 'Файли',
            type: 'file',
            isMultiple: true,
            // Двокроковий upload (SPEC-007 §2.1): FileInput вантажить кожен файл на
            // цей роут одразу (створює неприкріплену сироту), а збереження форми
            // прикріплює їх через `fileUuids` (див. form.eventHandlers нижче).
            // Показ на view-картці — лінки завантаження (FileInput open/download).
            uploadUrl: '/services/tasks/files',
            // Клієнт-ліміт = ФАКТ nginx `client_max_body_size` = 10 MB (хвіст 08.2):
            // nginx ріже >10 MB і віддає 413 ще до бекенда, тож показуємо юзеру 10,
            // а не 25. Бекенд-стеля лишається 25 MB (TaskFileUploadValidator, SPEC-007
            // §2.6) — свідома розбіжність app-стелі й інфра-факту; nginx НЕ правимо (§5).
            uploadMaxSizeMb: 10,
        },
    },
    /**
     * Двокроковий attach (SPEC-003 §5.8): мапимо щойно завантажені, ще
     * неприкріплені файли у масив `fileUuids` тіла POST/PATCH і прибираємо поле
     * `files` (інакше save-цикл ядра згорнув би масив файлів у один IRI —
     * file-folding у ResourceDetail). Прикріплюємо ЛИШЕ нові upload'и — інакше
     * повторно надіслати вже прикріплений uuid дало б 422 на перевірці кількості
     * (`loadOwnedUnattachedByUuids` не знайде його серед сиріт, SPEC-007 §2.1).
     *
     * Дискримінатор «новий файл» = **наявність поля `createdAt`** (хвіст 08.4).
     * Це НЕ крихка евристика, а точний наслідок серіалізаційного контракту (звірено
     * з `FileEntity`): `createdAt` живе ВИКЛЮЧНО у групі `file:read`, якою
     * серіалізується upload-відповідь (POST .../files → ApiGroups `file:read`); вже
     * прикріплені файли задача віддає групою `file:short:read` (ApiGroups контролерів
     * задачі), де `createdAt`/`createdBy`/`updatedAt` відсутні. Тож `createdAt` є ⇔
     * файл прийшов з upload-відповіді (новий), і його немає ⇔ файл уже прикріплений.
     *
     * Надійніший на вигляд set-difference (initialValue \ value) тут НЕ підходить:
     * ядровий FileInput кладе upload саме в `initialValue` (push), а `value` часто
     * аліасить той самий масив, тож різниця виходила б порожньою. Тому лишаємо
     * контрактний дискримінатор `createdAt`; інваріант, який його тримає, —
     * «задача НЕ вкладає файли групою `file:read`». Змінюватимемо цей інваріант —
     * оновити й дискримінатор тут.
     */
    form: {
        eventHandlers: {
            onBeforeTransformSendSaveRequest: {
                taskFileUuids: ({ values, formContext }: IFormEventContext): void => {
                    const toArray = (value: unknown): unknown[] =>
                        Array.isArray(value) ? value : value != null ? [value] : []

                    const byUuid = new Map<string, TaskFileLike>()
                    const collected = [...toArray(formContext?.getInitialValue?.('files')), ...toArray(values['files'])]

                    for (const raw of collected) {
                        const file: TaskFileLike = typeof raw === 'object' && raw !== null ? raw : {}
                        const uuid = file.uuid ?? (typeof raw === 'string' ? raw : undefined)
                        if (typeof uuid === 'string' && uuid.length > 0) {
                            byUuid.set(uuid, file)
                        }
                    }

                    values['fileUuids'] = Array.from(byUuid.values())
                        .filter((file) => Boolean(file.createdAt))
                        .map((file) => file.uuid)
                        .filter((uuid): uuid is string => typeof uuid === 'string')

                    delete values['files']
                },
            },
        },
    },
    /**
     * Таблиця списку.
     *
     * **Контракт значення в `render`:** перед рендером ядро проганяє кожен item
     * через `TransformItemDataByDefault` → `transformFieldValue`
     * (`Core/Features/UI/CrudResource`), тобто в `render` приходить уже
     * ГОТОВЕ значення, а не сире з API: `date` → «DD.MM.YYYY», `switch` →
     * «Так»/«Ні», `status` → кольоровий бейдж, об'єкт зв'язку → `fullName`,
     * масив зв'язків → «Імʼя, Імʼя». Тому власний `render` пишемо лише там, де
     * ядрового показу нам не досить, і в парі з `dataSourceNoTransform` —
     * інакше type-guard'и такого рендера отримають рядок замість об'єкта і
     * мовчки віддадуть заглушку.
     *
     * `dataSourceNoTransform` вимикає ядрову трансформацію точково:
     *   • `manager`/`responsibles` — щоб у таблиці стояв компактний `shortName`
     *     («Петро Мельник»), а не `fullName` з по батькові;
     *   • `deadline` — щоб дата була в укр. форматі «дд.мм.рррр», а не в
     *     глобальному `AppConfig.dateDefaultFormat` («YYYY-MM-DD»).
     * Решта колонок — на ядровому показі.
     */
    table: {
        showColumns: ['number', 'title', 'status', 'deadline', 'manager', 'responsibles', 'isPriority'],
        dataSourceNoTransform: ['manager', 'responsibles', 'deadline'],
        // Сортування — лише за скалярними полями (SPEC-008 §9): number/status/deadline/isPriority.
        // Колонки зв'язків (manager/responsibles) — без `sorter`, бо ядро вміє тільки `e.<field>`
        // і сортування за `manager.lastName` дає 500 (перевірено проти бекенда).
        columns: [
            {
                title: '№',
                dataIndex: 'number',
                key: 'number',
                width: 80,
                sorter: true,
                render: (v: number) => (typeof v === 'number' ? `№${v}` : '—'),
            },
            { title: 'Назва', dataIndex: 'title', key: 'title', render: (v: string) => v || '—' },
            // Статус — ядровий показ: бейдж бере колір з `options` поля
            // (TASK_STATUS_OPTIONS), тож стиль єдиний з рештою застосунку.
            { title: 'Статус', dataIndex: 'status', key: 'status', width: 150, sorter: true },
            {
                title: 'Дедлайн',
                dataIndex: 'deadline',
                key: 'deadline',
                width: 130,
                sorter: true,
                render: (v: string) => formatDate(v),
            },
            {
                title: 'Керівник',
                dataIndex: 'manager',
                key: 'manager',
                width: 180,
                render: (v: TaskUserRef) => userLabel(v),
            },
            {
                title: 'Виконавці',
                dataIndex: 'responsibles',
                key: 'responsibles',
                width: 220,
                render: (v: TaskUserRef[]) => (Array.isArray(v) && v.length ? v.map(userLabel).join(', ') : '—'),
            },
            // Пріоритет — ядровий показ `switch` («Так»/«Ні»). Прочерк на `false`
            // читався як «дані не завантажились», тому лишаємо явну відповідь.
            { title: 'Пріоритет', dataIndex: 'isPriority', key: 'isPriority', width: 110, sorter: true },
        ],
        scroll: { x: 'max-content' },
    },
    filter: {
        includeFields: ['status', 'manager', 'responsibles', 'deadline', 'isPriority'],
    },
    settings: {
        searchFields: ['number', 'title'],
    },
    // Ширина картки-дровера. Дефолт ядра — 500px (ResourceDetail.jsx:1052), при
    // якому single-column форма затісна, а заголовок «Завдання» + кнопки воркфлоу
    // (Старт/Пауза/Завершити) не вміщаються в шапці й перекриваються. Ставимо
    // комфортну ширину за патерном autostrada RequestResource (`drawer.width`).
    drawer: {
        width: 1530,
    },
    /**
     * Додаткові режими списку — Канбан і Календар (перенос фічі з ehs).
     *
     * Канонічний шлях без змін Core: `resource.headerPage` мержиться у props
     * headerPage-контексту (CrudResourceContextProvider), його eventHandlers
     * реєструє UIManager, і хендлер спрацьовує поряд із ядровим
     * `AddTableViewModeToList` — просто дописує варіанти у `variantItems`
     * тієї ж форми. Перемикач малює ядровий `PushModeListToExtra`
     * (Segmented з'являється, щойно режимів стає більше одного).
     */
    headerPage: {
        eventHandlers: {
            onAfterInit: {
                AddTaskViewModes: ({ headerPageContext }: { headerPageContext?: TaskHeaderPageContextLike }): void => {
                    const crudContext = headerPageContext?.storages?.crudContext
                    const listContext = crudContext?.listContext
                    if (!crudContext || !listContext) return
                    // Вкладені списки (вкладка «Підзавдання») вимикають режими:
                    // канбан/календар усередині картки — зайві
                    if (crudContext.settings?.['taskViewModes'] === false) return

                    // Синхронно, у тому ж onAfterInit-ланцюгу, що й ядровий
                    // AddTableViewModeToList: variantItems у ядрі НЕ observable,
                    // відкладені зміни Segmented не перемалюють.
                    //
                    // Порядок ключів свідомо «календар → канбан»: ядровий
                    // PushModeListToExtra робить options.reverse() (щоб таблиця,
                    // яку фіча додає останньою, стояла першою кнопкою), тож на
                    // екрані виходить [Таблиця, Канбан, Календар].
                    listContext.variantItems = {
                        ...(listContext.variantItems ?? {}),
                        calendar: {
                            label: 'Календар',
                            icon: AiOutlineCalendar,
                            template: LazyCalendarTemplate,
                            props: { crudContext },
                        },
                        kanban: {
                            label: 'Канбан',
                            icon: TbLayoutKanban,
                            template: LazyKanbanTemplate,
                            props: { crudContext },
                        },
                    }
                },

                /**
                 * Пункт «Повернути» в меню дій рядка.
                 *
                 * Ядро збирає меню подією `onTableMenuItemsBuild`, але
                 * верхньорівневі `eventHandlers` РЕСУРСУ воно в контекст не
                 * мерджить (CrudResourceContextProvider бере лише пропси
                 * компонента) — тож підписуємось тут, де crudContext уже є.
                 *
                 * Видимість — виключно `_permissions.restore`: бек дає це право
                 * лише адміністраторові й лише на видаленому завданні, тож
                 * окремої перевірки сторінки чи ролі тут не треба.
                 */
                AddTaskRestoreMenuItem: ({
                    headerPageContext,
                }: {
                    headerPageContext?: TaskHeaderPageContextLike
                }): void => {
                    const crudContext = headerPageContext?.storages?.crudContext
                    const eventManager = crudContext?.eventManager
                    if (!crudContext || !eventManager) return

                    eventManager.on('onTableMenuItemsBuild', 'TaskRestoreMenuItem', (payload) => {
                        const { menuItems, item } = payload as unknown as {
                            menuItems: TaskMenuItem[]
                            item: { uuid?: string; _permissions?: TaskPermissions }
                        }

                        if (item?._permissions?.restore !== true) return
                        if (menuItems.some((menuItem) => menuItem.key === 'restore')) return

                        menuItems.push({
                            key: 'restore',
                            sort: 250,
                            icon: <MdOutlineRestoreFromTrash style={{ fontSize: 18 }} />,
                            label: 'Повернути',
                            onClick: () => {
                                const uuid = item?.uuid
                                if (!uuid) return

                                RestoreTaskTask(String(uuid))
                                    .then(() => {
                                        notificationService.showSuccess('Готово', 'Завдання повернуто')
                                        crudContext.listContext?.actions?.load?.(true)
                                    })
                                    .catch(() =>
                                        notificationService.showError('Помилка', 'Не вдалося повернути завдання')
                                    )
                            },
                        })
                    })
                },
            },
        },
    },
    pageConfig: {
        title: 'Завдання',
        icon: <MdOutlineChecklist />,
    },
    /**
     * Картка перегляду: шаблон із вкладкою «Історія» (SPEC-008 §5.2) і кнопки
     * воркфлоу (§8.2). create/edit лишаються на дефолтному рендері ядра.
     *
     * Видимість кнопки = статус (SPEC-006 §4.2) ∧ `_permissions` (SPEC-005 §5).
     * Джерело правди прав — бекенд; фронт лише показує/ховає. `¬isRemoved` уже вшито
     * в `_permissions` (на видаленому всі мутуючі права = false).
     *
     *   | Кнопка    | Видимість                                              | Дія                              |
     *   |-----------|--------------------------------------------------------|----------------------------------|
     *   | Старт     | status ∈ {new,suspended,overdue} ∧ _permissions.start  | POST .../start   (N6)            |
     *   | Пауза     | status === in_process ∧ _permissions.suspend           | POST .../suspend (N7)            |
     *   | Завершити | status !== completed ∧ _permissions.complete           | модалка → POST .../complete (N8) |
     *   | Повернути | _permissions.restore (бек дає його лише на видаленому) | POST .../restore                 |
     *
     * «Пауза» — лише для in_process (SPEC-008 §13.1: бек дозволяє suspend і з
     * new/overdue, але «паузити» нестартоване недоречно — ховаємо).
     */
    detailConfig: {
        modeConfig: {
            // Той самий шаблон на edit/create — перегляд і редагування виглядають
            // однаково (двоколонковий макет + вкладки; запит UI-огляду 11).
            edit: { template: TaskFormTemplate },
            create: { template: TaskFormTemplate },
            view: {
                template: TaskFormTemplate,
                onButtons: ({ buttons, crudContext, content }: TaskOnButtonsParams): void => {
                    const depStore = { formContext: crudContext?.formContext, crudContext }

                    // Кнопка «Чат» — get-or-create групового чату завдання
                    // (перенос autostrada RequestResource; перша, як там).
                    // Видима кожному, хто бачить картку: доступ до картки вже
                    // item-scoped (_permissions.read), а право на чат бек
                    // перевіряє сам (TaskRule::READ у Create/ChatAction).
                    buttons['chat'] = {
                        label: 'Чат',
                        icon: <MdOutlineForum />,
                        size: 'middle',
                        priority: 'button',
                        sort: 0,
                        depStore,
                        depFn: ({ formContext }: TaskButtonDepParams): boolean => !!formContext?.values?.uuid,
                        // Лоадер живе НЕ тут: ButtonGroupStore копіює конфіг
                        // spread-ом і get loading() застигає — натомість дровер
                        // чату відкривається одразу з оверлей-спінером
                        onClick: () => {
                            const uuid = crudContext?.formContext?.values?.uuid
                            if (uuid) {
                                // Get-or-create чату завдання і відкриття дровера —
                                // весь ланцюг живе у ChatContainer (публічний фасад)
                                void openEntityChat('task', String(uuid))
                            }
                        },
                    }

                    buttons['start'] = {
                        label: 'Старт',
                        icon: <MdOutlinePlayArrow />,
                        size: 'middle',
                        priority: 'button',
                        sort: 1,
                        depStore,
                        depFn: ({ formContext }: TaskButtonDepParams): boolean => {
                            const status = formContext?.values?.status
                            const permissions: TaskPermissions = formContext?.values?._permissions ?? {}
                            return !!status && ['new', 'suspended', 'overdue'].includes(status) && !!permissions.start
                        },
                        onClick: () => crudContext?.eventManager?.emit('taskAction:start', { crudContext }),
                    }

                    buttons['suspend'] = {
                        label: 'Пауза',
                        icon: <MdOutlinePause />,
                        size: 'middle',
                        priority: 'button',
                        sort: 2,
                        depStore,
                        depFn: ({ formContext }: TaskButtonDepParams): boolean => {
                            const permissions: TaskPermissions = formContext?.values?._permissions ?? {}
                            return formContext?.values?.status === 'in_process' && !!permissions.suspend
                        },
                        onClick: () => crudContext?.eventManager?.emit('taskAction:suspend', { crudContext }),
                    }

                    buttons['complete'] = {
                        label: 'Завершити',
                        icon: <MdOutlineCheckCircle />,
                        size: 'middle',
                        priority: 'button',
                        sort: 3,
                        depStore,
                        depFn: ({ formContext }: TaskButtonDepParams): boolean => {
                            const permissions: TaskPermissions = formContext?.values?._permissions ?? {}
                            return formContext?.values?.status !== 'completed' && !!permissions.complete
                        },
                        onClick: () => crudContext?.eventManager?.emit('taskAction:openCompleteModal', { crudContext }),
                    }

                    // Єдина дія, доступна на видаленому завданні: решта кнопок
                    // сховані, бо там усі мутуючі права false.
                    buttons['restore'] = {
                        label: 'Повернути',
                        icon: <MdOutlineRestoreFromTrash />,
                        size: 'middle',
                        priority: 'button',
                        sort: 4,
                        depStore,
                        depFn: ({ formContext }: TaskButtonDepParams): boolean => {
                            const permissions: TaskPermissions = formContext?.values?._permissions ?? {}
                            return !!permissions.restore
                        },
                        onClick: () => crudContext?.eventManager?.emit('taskAction:restore', { crudContext }),
                    }

                    if (crudContext)
                        content?.push(<TaskViewActions key="task-view-actions" crudContext={crudContext} />)
                },
            },
        },
    },
    /*
     * Resource-level accessFn СВІДОМО НЕ задаємо (хвіст 05.2, рев'ю 06).
     *
     * Фактичний гейт доступу картки/списку задач — це:
     *   • Ship-глобальний `CrudResourceConfig.accessFn` (об'єктна сигнатура
     *     `({crudContext, accessMode, accessItem})`) → `CheckGlobalResourceAccessTask` +
     *     item-level `_permissions`;
     *   • page-level accessFn на «Видалених» (ховає create, делегує решту глобальному);
     *   • бекенд `_permissions` (на видаленому item update/delete=false).
     *
     * Ядро НІКОЛИ не викликає resource.accessFn позиційно: `CrudResourceStore.ts:87-96`
     * загортає його лише коли контекстний accessFn ще не заданий (а Ship-глобальний
     * задає його завжди) і при цьому передає ФІКСОВАНО `{accessMode:'read_collection'}`.
     * Тобто попередній `(mode, item)`-варіант був мертвим і оманливим — прибрано.
     */
}

/**
 * Реєстрація короткого ключа `Task` у ResourceManager.
 *
 * Ядровий авто-лоадер (`ResourceKernelLoader`) реєструє цей ресурс під секційним
 * ключем `Task.Task` (Section.Resource). Додатково виставляємо короткий ключ `Task`,
 * бо його вимагають:
 *   • self-select `parent` (`resource: 'Task'`) — резолвиться через `GetResourceTask`;
 *   • дзвіночок сповіщень — бекенд шле `entityCode='Task'` (SPEC-006 §7.3 / SPEC-008 §10),
 *     а `NotificationDrawer` передає `entityCode` напряму в `GetResourceTask` /
 *     `OpenResourceCardTask`, тобто ключ ресурсу має дорівнювати саме `Task`.
 */
resourceManager.set('Task', TaskResource)
